import React from 'react';
import Alert, { AlertProps } from '@amzn/awsui-components-console/alert';
import { ChangeControlSeverity } from 'src/generated/graphql';
import { formatDayMonthYearTimeUtc } from 'src/common/utils/dateFormatters';
import { ActiveChangeControlWindow } from './useChangeControlStatus';
import { STATUS_ICON_ARIA_LABEL_IDS } from './statusIconAriaLabel';
import { useTranslation } from 'src/i18n/shim/useTranslation';

/**
 * Alert severity per window severity, matching the app-wide banner: BLOCKED is an
 * error because a submission touching live strings is refused outright without an
 * approved override, RESTRICTED a warning because one is still possible with it.
 *
 * Exported for the same reason `toFlashbarItem` is — with the notice rendering
 * no header, this mapping and the status icon's aria-label it drives are the
 * whole behavioural difference between the two severities, and asserting it
 * through rendered Cloudscape class names would test the library rather than
 * this decision.
 */
export const ALERT_TYPE: Record<
  ActiveChangeControlWindow['severity'],
  AlertProps.Type
> = {
  [ChangeControlSeverity.Blocked]: 'error',
  [ChangeControlSeverity.Restricted]: 'warning',
};

interface ChangeControlEditNoticeProps {
  /**
   * The active window. `null` means no window is in effect (or the status could
   * not be read), in which case nothing renders — showing a freeze notice off the
   * back of absent calendar data would be exactly the fail-closed behaviour
   * `useChangeControlStatus` exists to avoid.
   */
  changeControlWindow: ActiveChangeControlWindow | null;
}

/**
 * Tells the user, inside an edit surface, that today falls inside a
 * change-control (peak-freeze) window and that submitting the resulting change
 * will require a valid MCM.
 *
 * Rendered inline in the edit modal next to `NoPiiNotice` rather than as a
 * dialog in front of it: the notice is advisory, so a confirm step bought
 * nothing but a click — the MCM requirement is enforced by
 * `SubmitTranslationModal` (which collects the id) and ultimately by the backend
 * submit path. Like the no-PII notice it is not dismissible, so it is on screen
 * for the whole edit rather than once.
 *
 * Deliberately header-less: the body already opens with the window's own reason,
 * and the severity is carried by the alert type and the status icon's
 * aria-label, so a header would only restate what is already on screen.
 *
 * Only the caller knows whether its edit is in scope for the MCM rule — a draft
 * string that is not live yet is exempt (design §8.3) — so the caller passes
 * `null` for an exempt edit.
 */
const ChangeControlEditNotice: React.FC<ChangeControlEditNoticeProps> = ({
  changeControlWindow,
}) => {
  const { t, locale } = useTranslation();

  if (!changeControlWindow) {
    return null;
  }

  const { severity, reason, windowEnd } = changeControlWindow;

  return (
    <Alert
      type={ALERT_TYPE[severity]}
      statusIconAriaLabel={t(STATUS_ICON_ARIA_LABEL_IDS[severity])}
    >
      {/* `reason` is the freeze name from the change-control calendar and is
          passed through untranslated; the surrounding sentence is a single
          resource so each locale controls its own word order. */}
      {t('cc-notice-content', {
        reason,
        until: formatDayMonthYearTimeUtc(locale, new Date(windowEnd)),
      })}
    </Alert>
  );
};

export default ChangeControlEditNotice;
